import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { heroChatBubbleLeftRight, heroPlus } from '@ng-icons/heroicons/outline';
import { phosphorSidebarFill, phosphorSidebarSimpleFill } from '@ng-icons/phosphor-icons/fill';
import { DialogModule } from 'primeng/dialog';
import { filter, firstValueFrom, Subscription } from 'rxjs';
import { OauthService, UserInfo } from '../../services/oauth.service';
import { Repo, RepoService } from '../../services/repo.service';
import { UserSettingsService } from '../../services/user-settings.service';
import { CreateRepoComponent } from '../dialogue/create-repo/create-repo.component';
import { SdtmStudiesApiService } from '../../sdtm/core/services/sdtm-studies-api.service';

type Chat = {
  id: string;
  label: string;
  date: string;
  source?: 'local' | 'cloud';
}
type QuickNavData = {
  newItemName: string;
  recentItemsName: string;
  items: Chat[];
  onClick: (id: string, source?: 'local' | 'cloud') => void;
  onNewItem: () => void;
}

@Component({
  selector: 'app-drawer',
  imports: [
    CommonModule,
    NgIcon,
    DialogModule,
    CreateRepoComponent
  ],
  providers: [
    provideIcons({ heroPlus, heroChatBubbleLeftRight, phosphorSidebarFill, phosphorSidebarSimpleFill })
  ],
  templateUrl: './drawer.component.html',
  styleUrl: './drawer.component.css'
})
export class DrawerComponent implements OnInit, OnDestroy {
  @ViewChild(CreateRepoComponent) createRepoComponent!: CreateRepoComponent;
  private routerSubscription: Subscription;
  private sessionLabelSubscription: Subscription | undefined;
  userInfo: UserInfo | null = null;
  data: QuickNavData | null = null;
  selectedRepoId = { id: '', local: false };
  isStudyConnected = false;

  constructor(
    private router: Router,
    private repoService: RepoService,
    private oauthService: OauthService,
    private userSettings: UserSettingsService,
    private studiesApi: SdtmStudiesApiService,
  ) {
    this.routerSubscription = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(async (event: NavigationEnd) => {
        const page = event.url.split('/')[1];
        await this.manageSidebar(page);
      });
  }

  ngOnInit(): void {
    this.oauthService.getUser().then(user => {
      this.userInfo = user;
    }).catch(error => {
      console.error('Error fetching user info:', error);
      this.userInfo = null;
    });

    // Listen for session label updates to refresh the chat list
    this.sessionLabelSubscription = this.repoService.sessionLabelUpdated$.subscribe(async (update) => {
      if (update && this.data) {
        // Add the new chat to the existing list instead of refetching all chats
        const newChat = {
          id: update.sessionId,
          label: update.label,
          date: new Date().toISOString(), // Current timestamp for new chat
        };

        // Add to the beginning of the list (most recent first)
        this.data.items = [newChat, ...this.data.items];
      }
    });
  }

  async manageSidebar(page: string) {
    if (!this.userInfo) {
      try {
        this.userInfo = await this.oauthService.getUser();
      } catch (error) {
        console.error('Error fetching user info before managing sidebar:', error);
        this.userInfo = null;
        console.warn('User info is not available, cannot manage sidebar.');
        return;
      }
    }
    switch (page) {
      case 'workspace':
        this.data = {
          newItemName: 'New Chat',
          recentItemsName: 'Recent Chats',
          items: await this.repoService.getChats(),
          onClick: (id: string) => {this.selectChat(id); this.closeDrawer();},
          onNewItem: () => window.location.reload(),
        };
        break;

      case '': {
        const repos = (await this.repoService.getRepos()).map((repo: Repo) => ({
          id: repo.id?.toString() || '',
          label: repo.name,
          date: new Date().toISOString(),
          source: repo.type.toLowerCase() as 'local' | 'cloud'
        }));
        this.data = {
          newItemName: 'New Repository',
          recentItemsName: 'Repository',
          // filtering repos by source to separate local and cloud
          items: [...repos.filter(r => r.source === 'local'),...repos.filter(r => r.source === 'cloud')],
          onClick: (id: string, source?: 'local' | 'cloud') => {
            this.selectRepo(id, source);
          },
          onNewItem: () => {
            this.createRepoComponent.createRepoVisible = true;
            this.closeDrawer();
          },
        };
        break;
      }

      case 'sdtm': {
        // List the SDTM study-pipeline studies (/workflows/sdtm/studies) so a
        // row's id matches the /sdtm/studies/:id detail it opens. The bearer
        // token is attached by sas2r's interceptor. The backend returns ALL
        // studies for admins, so filter to the current user's own here — the
        // drawer is a personal "your studies" sidebar.
        let items: Chat[] = [];
        try {
          const res = await firstValueFrom(this.studiesApi.list());
          const myId = this.userInfo?.user_id;
          items = res.items
            .filter((s) => s.user_id === myId)
            .map((s) => ({
              id: s.id,
              label: s.protocol_id || s.name,
              date: s.created_at,
            }));
        } catch {
          items = [];
        }
        this.data = {
          newItemName: 'New Study',
          recentItemsName: 'Studies',
          items,
          onClick: (id: string) => {
            this.router.navigate(['/sdtm', 'studies', id]);
            this.closeDrawer();
          },
          onNewItem: () => {
            this.router.navigate(['sdtm/studies/new']);
            this.closeDrawer();
          },
        };
        break;
      }

      default:
        this.data = null;
    }
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
    if (this.sessionLabelSubscription) {
      this.sessionLabelSubscription.unsubscribe();
    }
  }

  selectChat(id: string) {
        this.repoService.newSession(id);
  }

  async selectRepo(id: string, source?: 'local' | 'cloud') {
    if (source === 'local') {
      this.selectedRepoId = { id, local: true };
    } else if (source === 'cloud') {
      this.selectedRepoId = { id, local: false };
    }

    if (this.selectedRepoId.local) {
      this.createRepoComponent.onSelectedRepoDialogShow(this.selectedRepoId.id);
      this.closeDrawer();
    } else {
      const selectedRepo = this.data?.items.find(item => item.id === id);
      const label = selectedRepo?.label || '';
      this.createRepoComponent.connectCloudExistingRepo(label, id).then(() => {
        this.closeDrawer();
        this.isStudyConnected = true;
      });
    }
  }

  closeDrawer() {
    const drawerCheckbox = document.getElementById('my-drawer') as HTMLInputElement;
    if (drawerCheckbox) {
      drawerCheckbox.checked = false;
    }
  }
}
